/**
 * Billing Types
 * TypeScript interfaces for subscription billing features
 *
 * Based on backend API contract: docs/subscriptions/07_api_contract.md
 * UI Design: docs/subscriptions/UI_DESIGN.md
 */

/**
 * Plan tier options
 */
export type PlanTier = 'free' | 'growth' | 'professional' | 'business'

/**
 * Subscription status (from Stripe)
 */
export type SubscriptionStatus =
  | 'active'       // Paid subscription in good standing
  | 'trialing'     // In trial period, not yet charged
  | 'past_due'     // Payment failed, access restricted
  | 'canceled'     // Subscription ended or ending
  | 'incomplete'   // Checkout started but not completed
  | 'unpaid'       // Payment failed, no access

/**
 * Billing period
 */
export type BillingPeriod = 'monthly' | 'annual' | null

/**
 * Payment status
 */
export type PaymentStatus = 'succeeded' | 'failed' | 'pending' | 'n/a'

/**
 * Pending subscription change (scheduled downgrade/upgrade)
 */
export interface PendingChange {
  newTier: string
  effectiveDate: number  // Unix timestamp
}

/**
 * Current plan data (GET /billing/current-plan)
 */
export interface CurrentPlan {
  tier: PlanTier
  status: SubscriptionStatus
  billingPeriod: BillingPeriod
  skuLimit: number
  smartimportLimit: number  // -1 = unlimited
  currentPeriodEnd: number | null  // Unix timestamp
  paymentStatus: PaymentStatus
  trialEnd: number | null  // Unix timestamp
  pendingChange?: PendingChange
}

/**
 * SKU usage metrics
 */
export interface SkuUsage {
  current: number
  limit: number
  percentage: number  // 0-100
  inGracePeriod: boolean
  graceUntil?: number  // Unix timestamp
  targetValue?: number  // Target SKU count during grace period
}

/**
 * SmartImport usage metrics
 */
export interface SmartImportUsage {
  current: number
  limit: number | null  // null if unlimited
  percentage: number  // 0-100
  resetsOn: string  // ISO date (YYYY-MM-DD)
  unlimited: boolean
}

/**
 * Usage metrics data (GET /billing/usage)
 */
export interface UsageMetrics {
  skuCount: SkuUsage
  smartimportRuns: SmartImportUsage
}

/**
 * Subscription status check (GET /billing/subscription-status)
 */
export interface SubscriptionStatusData {
  isActive: boolean
  status: SubscriptionStatus
  requiresPayment: boolean
  tier: string
}

/**
 * Checkout session response (POST /billing/checkout-session)
 */
export interface CheckoutSessionData {
  checkoutUrl: string
  sessionId: string
}

/**
 * Customer portal response (POST /billing/portal-session)
 */
export interface PortalSessionData {
  portalUrl: string
}

/**
 * Plan tier details for UI display
 */
export interface PlanTierDetails {
  tier: PlanTier
  displayName: string
  priceMonthly: number
  priceAnnual?: number
  skuLimit: number
  smartimportLimit: number | 'Unlimited'
  features: string[]
  recommended?: boolean
}

/**
 * Status badge color mapping (Ant Design Badge status)
 */
export const STATUS_COLORS: Record<SubscriptionStatus, 'success' | 'processing' | 'warning' | 'error' | 'default'> = {
  active: 'success',
  trialing: 'processing',
  past_due: 'warning',
  canceled: 'default',
  incomplete: 'warning',
  unpaid: 'error'
}

/**
 * Status text mapping (for user display)
 * Updated per Inara's feedback: "Trial" -> "Trial Active"
 */
export const STATUS_TEXT: Record<SubscriptionStatus, string> = {
  active: 'Active',
  trialing: 'Trial Active',
  past_due: 'Payment Due',
  canceled: 'Canceled',
  incomplete: 'Setup Incomplete',
  unpaid: 'Suspended'
}

/**
 * Plan tier configurations for display
 */
export const PLAN_TIERS: Record<PlanTier, PlanTierDetails> = {
  free: {
    tier: 'free',
    displayName: 'Free',
    priceMonthly: 0,
    skuLimit: 1000,
    smartimportLimit: 10,
    features: [
      'Basic pricing rules',
      'CSV export',
      'Community support'
    ]
  },
  growth: {
    tier: 'growth',
    displayName: 'Growth',
    priceMonthly: 29,
    priceAnnual: 290,  // 2 months free
    skuLimit: 2500,
    smartimportLimit: 'Unlimited',
    features: [
      'All data exports (CSV, Excel, API)',
      'Email + Chat support',
      '1-year historical data'
    ],
    recommended: true
  },
  professional: {
    tier: 'professional',
    displayName: 'Professional',
    priceMonthly: 49,
    priceAnnual: 490,
    skuLimit: 5000,
    smartimportLimit: 'Unlimited',
    features: [
      'API Access',
      '2-year historical data',
      'Priority support'
    ]
  },
  business: {
    tier: 'business',
    displayName: 'Business',
    priceMonthly: 99,
    priceAnnual: 990,
    skuLimit: 10000,
    smartimportLimit: 'Unlimited',
    features: [
      'Unlimited history',
      'Phone support',
      'Dedicated account manager (optional)',
      'Custom integrations'
    ]
  }
}
